import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Form, Formik, Field, FormikValues } from 'formik';
import { useStoreState, useStoreActions } from '../../hooks/easyPeasyHooks';
import { CheckVerificationTokenInput, Customer, CustomerSearchInput, Maybe } from '../../gql/graphql';
import { formatPhoneNumber } from '../../helpers/phoneNumber/phoneNumber';
import { useTrack, usePage, useIdentify } from '../../hooks/segment';
import { CustomerLookupEvents } from '../../state-machine/types/customerLookupMachine.types';
import PageLoader from '../../components/pageLoader/PageLoader';
import FormWrapper from '../../components/formWrapper/FormWrapper';
import { colors, fontFamilies } from '../../components/baseStyle/Variables';
import TextField from '../../components/inputs/TextField';
import Button from '../../components/button/Button';
import Link from '../../components/Link';
import styled from '@emotion/styled';

type OTPCheckProps = {
  onNext: any;
};

const ReEnterPhoneWrapper = styled.div`
  color: ${colors.darkBlue};
  font-family: ${fontFamilies.interLight};
  font-size: 0.75rem;
  line-height: 125%;
  text-align: left;
  margin: 0 0 0.75rem 0;

  & strong,
  & a {
    font-family: ${fontFamilies.interSemiBold};
  }
`;

const OTPCheck: React.FC<OTPCheckProps> = ({ onNext }) => {
  // computed cannot be deconstructed, known bug
  const { checkVerificationToken, setCheckTokenErrors } = useStoreActions((actions) => actions.auth);
  const { customerSearchByCellPhone } = useStoreActions((actions) => actions.applicationForm);
  const { queryOrderPreAuth } = useStoreActions((actions) => actions.order);
  const { highestSupportedApplicationMilestone } = useStoreState((state) => state.featureFlags);

  const { checkTokenErrors, wasCodeValidated } = useStoreState((state) => state.auth);

  const { customers, customerFlowType, wasEligibilityUpdated, cellPhone, eligibilityDetails } = useStoreState((state) => state.applicationForm);
  const isCurrentApplyTypeOTB = useStoreState((state) => state.applicationForm.isCurrentApplyTypeOTB);
  const { order, orderId } = useStoreState((state) => state.order);

  const track = useTrack();
  const identify = useIdentify();
  const page = usePage();

  const formRef = useRef<FormikValues>();

  const [showLoader, setShowLoader] = useState(false);
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    page('OTP Check');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    formRef?.current?.resetForm();
    document.getElementsByName('code')[0]?.focus();
  });

  const handleSubmit = (e: any) => {
    setShowLoader(true);
    setCheckTokenErrors([]);

    const payload: CheckVerificationTokenInput = {
      cellPhone: cellPhone || '',
      token: e?.code,
      orderId: orderId!,
    };

    checkVerificationToken(payload);
  };

  useEffect(() => {
    if (wasCodeValidated) {
      const customerCheckPayload: CustomerSearchInput = {
        cellPhone: cellPhone,
        orderId: orderId!,
        analytics: {
          campaignMedium: new URLSearchParams(document.location.search.substring(1)).get('cm') || undefined,
          campaignSource: new URLSearchParams(document.location.search.substring(1)).get('cs') || undefined,
        },
      };

      customerSearchByCellPhone(customerCheckPayload);
    }
  }, [wasCodeValidated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    trackCustomerSearchResult(customers, track, identify);
  }, [customers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (wasCodeValidated && customerFlowType && wasEligibilityUpdated) {
      track('Mobile Number Confirmed');

      // must declare a function to use await
      const refreshOrder = async () => {
        await queryOrderPreAuth(orderId!);

        onNext({
          type: CustomerLookupEvents.CHECK_OTP,
          data: {
            customerFlowType,
            wasCodeValidated,
            isCurrentApplyTypeOTB,
            order,
            eligibilityDetails,
            highestSupportedApplicationMilestone,
          },
        });
      };

      refreshOrder();
    }
  }, [wasCodeValidated, customerFlowType, wasEligibilityUpdated, highestSupportedApplicationMilestone]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (checkTokenErrors.length > 0) {
      if (checkTokenErrors.some((error) => error.__typename === 'MaxAttempts')) {
        setErrorText(`Too many incorrect verification attempts.`);
      } else {
        setErrorText(
          `This verification code is invalid, please check that you have entered it correctly and try again or click here to have a new verification code sent to the phone number ending in ${cellPhone.slice(
            -4,
          )}.`,
        );
      }

      setShowLoader(false);
    }
  }, [checkTokenErrors]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Suspense fallback={<PageLoader />}>
      <FormWrapper>
        <h2>Check your phone</h2>
        <h6>
          Please enter the verification code we sent to <strong style={{ fontFamily: fontFamilies.interBold }}>{formatPhoneNumber(cellPhone)}</strong>.
        </h6>

        <Formik
          onSubmit={handleSubmit}
          initialValues={{ code: '' }}
          // @ts-ignore
          innerRef={formRef}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {() => (
            <Form>
              <Field
                name="code"
                id="code"
                type="text"
                inputMode="numeric"
                component={TextField}
                useRawValue={true}
                label="Enter 4-digit code"
                autoFocus
                generalErrorMessage={() => errorText}
                cleaveOptions={{
                  blocks: [4],
                  numericOnly: true,
                }}
              />
              <Button type="submit" data-testid="nextButton" dataLoading={showLoader}>
                Continue
              </Button>
            </Form>
          )}
        </Formik>
        <ReEnterPhoneWrapper>
          Didn't get a code? <Link onClick={() => onNext({ type: CustomerLookupEvents.BACK })} linkText="Re-enter phone number" /> or ask your sales
          representative for assistance.
        </ReEnterPhoneWrapper>
      </FormWrapper>
    </Suspense>
  );
};

export default OTPCheck;

interface IdentifyArgs {
  customerId: number;
  emailAddress: Maybe<string> | undefined;
  cellPhone: string;
  firstName: string;
  lastName: string;
}
export function trackCustomerSearchResult(
  customers: Customer[] | null,
  track: (message: string, payload?: Object) => void,
  identify: ({ customerId, emailAddress, cellPhone, firstName, lastName }: IdentifyArgs) => void,
) {
  if (customers && customers.length) {
    track('Phone Number Matched', { numRecords: customers?.length });
  }
  if (customers?.length === 1 && customers[0]?.id) {
    identify({
      customerId: customers[0].id,
      emailAddress: customers[0].emailAddress,
      cellPhone: customers[0].cellPhone,
      firstName: customers[0].firstName,
      lastName: customers[0].lastName,
    });
  } // otherwise, continue to use the anonymous identify
}
